/* eslint-disable no-barrel-files/no-barrel-files -- Re-export for compatibility */
import {
  type ChildrenProps,
  Item as UIKitFormItem,
  type ItemProps,
} from "@circle-react-uikit/Form/Item";
import { get } from "lodash";
import type { ReactElement, ReactNode } from "react";
import { Children, cloneElement, isValidElement } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

export type {
  ItemProps,
  ChildrenProps,
  ChildrenWrapperProps,
} from "@circle-react-uikit/Form/Item";

interface CompassChildrenProps extends Omit<ChildrenProps, "label"> {
  description?: ReactNode;
  error?: string;
  horizontalLayoutRatio?: string;
  invalid?: boolean;
  label?: ReactNode;
  labelPosition?: "start" | "end";
  labelTooltip?: ReactNode;
  layout?: "horizontal" | "vertical";
}

const SUPPORTED_CHILD_DISPLAY_NAMES = new Set([
  "Form.Input",
  "FormInput",
  "LinkInput",
  "Form.Select",
  "TextSelect",
  "Form.TextSelect",
  "Form.SelectNative",
  "SimpleDropdown",
  "Form.Checkbox",
  "Form.ToggleSwitch",
  "SpaceSelect",
  "SpaceSelectCompass",
  "SpaceGroupSelect",
  "SpaceGroupSelectCompass",
  "AiAgentSelect",
  "AiAgentSelectCompass",
  "Form.CommunityMemberSelect",
  "Form.ColorPicker",
  "TextSelect",
  "EmbedTextArea",
  "CodeInput",
  "SenderEmailInput",
  "PaywallCurrencyAmountFormInput",
  "Select",
  "Form.TextArea",
  "CustomLocationInput",
  "NumberInput",
  "CustomSelect",
  "CustomCheckbox",
]);

const isSupportedChild = (child: ReactElement): boolean => {
  const displayName = (child.type as { displayName?: string })?.displayName;
  return Boolean(displayName && SUPPORTED_CHILD_DISPLAY_NAMES.has(displayName));
};

export const Item = (
  props: Readonly<ItemProps & { shouldForceV4FormItem?: boolean }>,
) => {
  const {
    formState: { errors },
  } = useFormContext();

  const { shouldForceV4FormItem = false } = props;

  const { children } = props;

  const childArray = Children.toArray(children).filter(isValidElement);
  const hasAllSupportedChildren =
    childArray.length > 0 && childArray.every(isSupportedChild);

  if (!hasAllSupportedChildren && !shouldForceV4FormItem) {
    return <UIKitFormItem {...props} />;
  }

  const itemProps = props as ItemProps & {
    layout?: "horizontal" | "vertical";
  };

  const {
    // Wrapper props
    className,
    hiddenField: isHiddenField,
    hideBorder: shouldHideBorder,
    isFirstChild,
    fullWidth: isFullWidth,
    resizeDisabled: isResizeDisabled,
    prefix,
    "data-testid": dataTestId,
    // Child props
    name = "",
    label,
    description,
    translationRoot,
    hideDescription: shouldHideDescription,
    hideLabel: shouldHideLabel,
    hideError: shouldHideError,
    hidePlaceholder: shouldHidePlaceholder,
    placeholder = "",
    rules,
    required: isRequired = false,
    forceRequiredLabel: shouldForceRequiredLabel = false,
    id,
    inline: isInline = false,
    inlineReverse: isInlineReverse = false,
    layout,
    horizontalLayoutRatio,
    transformValue,
    tooltipText,
    hasNoPadding,
  } = itemProps;

  const resolvedLayout =
    layout ?? (isInline || isInlineReverse ? "horizontal" : "vertical");

  const error = get(errors, name);

  const localLabel =
    label || (translationRoot && t(`${translationRoot}.${name}`));

  const translatedDescription =
    translationRoot &&
    t(`${translationRoot}.${name}_description`, { defaultValue: "" });

  const localDescription = description || translatedDescription || undefined;

  let localPlaceholder: string | undefined;
  if (!shouldHidePlaceholder) {
    localPlaceholder =
      placeholder ||
      (translationRoot && t(`${translationRoot}.${name}_placeholder`));
  }

  const hasRequiredRule = Boolean(
    rules && typeof rules.required === "boolean" && rules.required,
  );

  const wrapperClassName = classNames(
    "block",
    {
      relative: Boolean(prefix),
      "pt-0": !isHiddenField && isFirstChild,
      "pt-6": !isHiddenField && !isFirstChild,
      "w-full min-w-0": isFullWidth,
      "border-none": shouldHideBorder,
      "resize-none": isResizeDisabled,
      "p-0": hasNoPadding,
    },
    className,
  );

  return (
    <div className={wrapperClassName} data-testid={dataTestId}>
      {prefix && (
        <div className="absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-center">
          {prefix}
        </div>
      )}
      {Children.map(children, child => {
        if (!isValidElement<CompassChildrenProps>(child)) {
          return null;
        }

        return cloneElement(child, {
          name,
          rules,
          id,
          placeholder: localPlaceholder,
          transformValue,
          required: isRequired || hasRequiredRule || shouldForceRequiredLabel,
          label: shouldHideLabel
            ? undefined
            : (localLabel ?? child.props.label),
          ...(!shouldHideDescription && localDescription
            ? { description: localDescription }
            : {}),
          labelPosition: isInlineReverse ? "end" : "start",
          layout: resolvedLayout,
          error: shouldHideError
            ? undefined
            : (error?.message as string | undefined),
          invalid: shouldHideError ? false : Boolean(error),
          labelTooltip: tooltipText,
          horizontalLayoutRatio,
        });
      })}
    </div>
  );
};

Item.displayName = "Form.Item";
